// F13a (FR-ST21/ST25): the per-recording public-viewing (egress) cap, as one compact row in the
// share dialog's "Link protection" list. It used to be a "Public viewing" card on the watch page
// itself, with a usage meter and an "About N views" count; that was viewer-analytics-shaped
// (FR-EN7) and out of place on the recording view, so it was removed. What survives is the one
// action FR-ST25 needs a home for — an org admin raising or lowering this recording's cap, never
// above the org cap — next to the other controls that only bite on a public link (caps never
// apply to org members, FR-ST26). Editors who can't set the cap see nothing here.
// GET/PUT /grackles/{id}/egress.
import { useEffect, useState } from "react";
import { Icon, Input } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import { Button } from "../components/ui";
import { formatBytes } from "../library/format";

/** The subset of `GET /grackles/{id}/egress` this row reads (the response also carries usage
 * and an approximate view count, which are deliberately not shown). */
export interface EgressOut {
  state: "ok" | "over_soft" | "hard_stopped" | "recovering";
  soft_bytes: number | null;
  hard_bytes: number | null;
  org_soft_bytes: number | null;
  org_hard_bytes: number | null;
  can_set_cap: boolean;
}

const GB = 1024 ** 3;
/** FR-ST23a: the hard ceiling defaults to 5x the soft cap. */
const HARD_MULTIPLIER = 5;

const STATE_NOTE: Partial<Record<EgressOut["state"], string>> = {
  over_soft: "Over the soft limit — still playing.",
  hard_stopped: "Public viewing is stopped.",
  recovering: "Public viewing is resuming.",
};

/** The soft cap (GB) the form asks for → the request body, never above the org cap. */
export function capRequest(softGb: string, orgHard: number | null): { soft_bytes: number; hard_bytes: number } | null {
  const n = Number(softGb.trim());
  if (!softGb.trim() || !Number.isFinite(n) || n <= 0) return null;
  const soft = Math.round(n * GB);
  const hard = Math.max(soft, orgHard == null ? soft * HARD_MULTIPLIER : Math.min(soft * HARD_MULTIPLIER, orgHard));
  return { soft_bytes: soft, hard_bytes: hard };
}

export default function BandwidthLimitRow({ grackleId }: { grackleId: string }) {
  const [data, setData] = useState<EgressOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [softGb, setSoftGb] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function load() {
    try {
      setData(await api<EgressOut>("GET", `/grackles/${grackleId}/egress`));
    } catch {
      // Not for this caller, or not reachable: the row simply isn't offered.
      setData(null);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grackleId]);

  if (!data?.can_set_cap) return null;

  const request = capRequest(softGb, data.org_hard_bytes);
  const orgLimit = data.org_soft_bytes != null ? formatBytes(data.org_soft_bytes) : null;
  const overOrg = request != null && data.org_soft_bytes != null && request.soft_bytes > data.org_soft_bytes;
  const current =
    data.soft_bytes != null
      ? `Soft limit ${formatBytes(data.soft_bytes)}${data.hard_bytes != null ? `, stops at ${formatBytes(data.hard_bytes)}` : ""}.`
      : "No limit set.";

  async function save() {
    if (!request || overOrg) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await api("PUT", `/grackles/${grackleId}/egress`, request);
      setSaved(true);
      setSoftGb("");
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save the limit.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="share__protection">
      <Icon name="chart-donut" />
      <div className="share__protection-body">
        <div className="share__protection-label">Bandwidth limit</div>
        <div className="gk-caption">
          {current} {STATE_NOTE[data.state] ?? ""} Applies to viewers outside your org.
        </div>
        <form
          className="share__protection-controls"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Input
            inputMode="decimal"
            placeholder="Soft limit (GB)"
            aria-label="Soft bandwidth limit in GB"
            value={softGb}
            onChange={(e) => {
              setSoftGb(e.target.value);
              setSaved(false);
            }}
            disabled={saving}
            wrapperStyle={{ width: 140 }}
          />
          <Button type="submit" variant="secondary" size="sm" loading={saving} disabled={!request || overOrg}>
            Save
          </Button>
        </form>
        {overOrg ? (
          <div className="gk-caption" role="alert">
            That is above your org's limit of {orgLimit}.
          </div>
        ) : error ? (
          <div className="gk-caption" role="alert">
            {error}
          </div>
        ) : saved ? (
          <div className="gk-caption" role="status">
            Limit saved.
          </div>
        ) : (
          <div className="gk-caption">
            {orgLimit ? `Up to your org's limit of ${orgLimit}. ` : ""}Public viewing stops at {HARD_MULTIPLIER}× this.
          </div>
        )}
      </div>
    </div>
  );
}
